import restApi from "../../../services/restApi";
import endpoints from "../../../config/endpoints";
import { OrderFormState } from "../../../types/types";

const toEnglishDigits = (value: any) =>
  String(value ?? "")
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .trim();

// Returns a message for the first missing/invalid field, or null when the
// form can be saved. New orders also need the customer and address, which
// are created on the fly when they don't exist yet.
const validateOrderForm = (form: OrderFormState, isNew: boolean): string | null => {
  if (isNew) {
    if (!/^09\d{9}$/.test(toEnglishDigits(form.user?.phoneNumber))) {
      return "شماره تلفن مشتری را به درستی وارد کنید";
    }
    if (!form.user?.name?.trim()) {
      return "نام مشتری را وارد کنید";
    }
    if (!form.address?.id && !form.address?.description?.trim()) {
      return "آدرس مشتری را وارد کنید";
    }
  }
  if (!/^\d{4}\/\d{2}\/\d{2}$/.test(toEnglishDigits(form.date))) {
    return "تاریخ سفارش را انتخاب کنید";
  }
  if (form.time === "" || form.time === null || form.time === undefined || isNaN(Number(form.time))) {
    return "ساعت سفارش را انتخاب کنید";
  }
  if (!form.orderServices?.length) {
    return "حداقل یک خدمت انتخاب کنید";
  }
  return null;
};

const requestError = (res: any, fallback: string) =>
  typeof res?.data == "string" ? res.data : fallback;

export const orderService = {
  fetchColors: async () => {
    try {
      const res = await restApi(endpoints.color.index, true).get();
      return { success: true, data: res.data };
    } catch (error) {
      console.error("Error fetching colors:", error);
      return { success: false, error };
    }
  },

  fetchUser: async (phoneNumber: string) => {
    try {
      const res = await restApi(endpoints.user.findBy, true).get({
        phoneNumber,
      });
      return { success: true, data: res.data };
    } catch (error) {
      console.error("Error fetching user:", error);
      return { success: false, error };
    }
  },

  fetchOrder: async (id: string) => {
    try {
      const res = await restApi(endpoints.order.single + id, true).get();

      // Transform the data to match our form structure
      const formattedData = {
        date: res.data?.date,
        time: res.data?.fromTime,
        toTime: res.data?.toTime,
        code: res.data?.code,
        status: res.data?.status,
        finalPrice: res.data.finalPrice,
        price: Number(res.data?.price),
        serviceId: res.data?.serviceId,
        transportation: res.data?.transportation,
        discountAmount: res.data?.discountAmount,
        orderServices: res.data?.orderServices.map((e) => ({
          ...e,
          colors: e.colors.map((j) => j.id),
        })),
        address: res.data?.user?.addresses?.find(
          (e) => e.id === res.data?.addressId,
        ),
        addressId: res.data?.addressId,
        user: res.data?.user,
        userId: res.data?.userId,
        createdAt: res.data?.createdAt,
        isMulti: res.data?.isMulti,
        isUrgent: res.data?.isUrgent,
        finalImage: res.data?.finalImage,
        doneDate: res.data?.doneDate,
        startDate: res.data?.startDate,
        taxAmount: res.data?.taxAmount,
        shouldUseWallet: false,
        shouldSendWorkerSMS: false,
        payment: res.data?.payment,
        isWebsite: res.data?.isWebsite,
        worker: res.data?.worker,
        workerId: res.data?.workerId,
        workerPercent: res.data?.workerPercent,
      };

      return { success: true, data: formattedData };
    } catch (error) {
      console.error("Error fetching order:", error);
      return { success: false, error };
    }
  },

  saveOrder: async (form: OrderFormState, id?: string) => {
    try {
      let userRes, addressRes;

      const validationError = validateOrderForm(form, !id);
      if (validationError) {
        return { success: false, error: validationError };
      }

      // If creating a new order, handle user and address creation/update.
      // A customer that doesn't exist yet is created here.
      if (!id) {
        // Only compare the national code with the phone when one was entered
        if (!form.user?.isVerified && form.user?.nationalCode) {
          const verifyNationalCode = await restApi(
            process.env.REACT_APP_BASE_URL + "/admin/user/verify",
          ).post({
            phoneNumber: toEnglishDigits(form.user.phoneNumber),
            nationalCode: toEnglishDigits(form.user.nationalCode),
          });

          if (verifyNationalCode.code === 1005) {
            const shouldContinue = window.confirm(
              "کد ملی با شماره تلفن تطابق ندارد آیا به هرحال سفارش ثبت شود؟",
            );
            if (!shouldContinue) {
              return { success: false, error: "عملیات لغو شد" };
            }
          }
        }

        userRes = await restApi(
          process.env.REACT_APP_BASE_URL +
            "/admin/user/basic/" +
            (form?.user?.id || ""),
        ).post({
          name: form.user.name,
          lastName: form.user.lastName,
          nationalCode: toEnglishDigits(form.user.nationalCode),
          phoneNumber: toEnglishDigits(form.user.phoneNumber),
          isVerified: form.user.isVerified,
        });
        if (!userRes?.data?.id) {
          return { success: false, error: requestError(userRes, "ثبت مشتری ناموفق بود") };
        }

        addressRes = await restApi(
          process.env.REACT_APP_BASE_URL +
            "/admin/address/basic/" +
            (form?.address?.id || ""),
        ).post({
          title: form.address.title,
          phoneNumber: form.address.phoneNumber,
          description: form.address.description,
          vahed: form.address.vahed,
          pelak: form.address.pelak,
          userId: userRes.data?.id,
          longitude: form.address.longitude,
          latitude: form?.address?.latitude,
        });
        if (!addressRes?.data?.id) {
          return { success: false, error: requestError(addressRes, "ثبت آدرس ناموفق بود") };
        }
      }

      // Create or update the order
      const res = await restApi(
        process.env.REACT_APP_BASE_URL + "/admin/order/basic/" + (id || ""),
      ).post({
        date: form.date,
        time: form.time,
        toTime: Number(form.toTime),
        status: form.status,
        finalPrice: form.finalPrice,
        price: form.price,
        serviceId: form.serviceId,
        discountAmount: form.discountAmount,
        transportation: form.transportation,
        addressId: form.address.id || addressRes?.data.id,
        userId: form.user?.id || userRes?.data?.id,
        isMulti: form.isMulti,
        isUrgent: form.isUrgent,
        shouldSendWorkerSMS: form.shouldSendWorkerSMS,
        workerId: form.workerId,
        workerPercent: Number(form.workerPercent),
        taxAmount: Number(form.taxAmount),
      });
      if (!res?.data?.id) {
        return { success: false, error: requestError(res, "ثبت سفارش ناموفق بود") };
      }

      // Update order services and payment
      await Promise.all([
        restApi(
          process.env.REACT_APP_BASE_URL +
            "/admin/order/products/" +
            res.data.id,
        ).put({
          services: form.orderServices.map((e) => ({
            serviceId: e.serviceId,
            count: e.count,
            colors: e.colors,
          })),
        }),
        restApi(
          process.env.REACT_APP_BASE_URL +
            "/admin/order/payment/" +
            res.data.id,
        ).put({
          method: form?.payment?.method,
          finalPrice: form?.payment?.finalPrice,
          shouldUseWallet: form?.payment?.shouldUseWallet,
          refId: form?.payment?.refId,
          description: form?.payment?.description,
        }),
      ]);

      return { success: true, data: res.data };
    } catch (error) {
      console.error("Error saving order:", error);
      return { success: false, error: error.message || "خطا در ذخیره سفارش" };
    }
  },
  getWorkers: async (form: OrderFormState) => {
    try {
      return await restApi(endpoints.order.relatedWorkers).post({
        date: form.date,
        attributes: form.orderServices,
        address: form.address,
        fromTime: form.time,
        toTime:
          form.toTime ||
          Number(
            form?.orderServices?.reduce(
              (acc, curr) => acc + Number(curr?.service?.section),
              Number(form.time),
            ),
          ),
      });
    } catch (error) {
      console.error("Error sending portal:", error);
      return {
        success: false,
        error: error.message || "خطا در ارسال پورتال",
      };
    }
  },
  sendPortal: async (id: string, finalPrice, method, description, refId) => {
    try {
      const res = await restApi(
        process.env.REACT_APP_BASE_URL + "/admin/order/sendPortal/" + id,
      ).post({
        finalPrice,
        method,
        description,
        refId,
      });
      return {
        success: true,
        data: res.data,
      };
    } catch (error) {
      console.error("Error sending portal:", error);
      return {
        success: false,
        error: error.message || "خطا در ارسال پورتال",
      };
    }
  },

  sendFeedbackSMS: async (id: string) => {
    try {
      const res = await restApi(
        process.env.REACT_APP_BASE_URL + "/admin/order/sendFeedbackSMS/" + id,
      ).post({});
      if (res.code == 200) {
        return {
          success: true,
          data: res.data,
        };
      } else return { success: false };
    } catch (error) {
      console.error("Error sending portal:", error);
      return {
        success: false,
        error: error.message || "خطا در ارسال پیام نظرسنجی",
      };
    }
  },
};
